import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";
import chatModel from "./chatModel";
import HandymanModel from "./handymanModel";
import { getTenant } from "./tenantModel";

// Repairs are stored in maintenanceTickets collection
const COLLECTION_NAME = "maintenanceTickets";

function normalizeRepairCategory(category) {
  const raw = String(category || "").trim();
  const lower = raw.toLowerCase();
  if (lower === "appliance") return "appliances";
  return raw || "other";
}

/**
 * Helper function to create or update maintenance group chat when needed
 * @param {Object} repair - Repair/ticket data
 * @param {string} landlordId - Landlord ID
 */
async function ensureMaintenanceGroupChat(repair, landlordId) {
  try {
    // Only create chat if there's a tenant ID
    if (!repair.tenantId) {
      return;
    }

    // Get tenant info
    let tenantInfo;
    try {
      const tenant = await getTenant(repair.tenantId);
      tenantInfo = {
        name:
          `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim() ||
          "Tenant",
        unitNumber: repair.unitNumber || tenant.unitNumber || null,
      };
    } catch (error) {
      console.warn("Could not fetch tenant info for group chat:", error);
      tenantInfo = {
        name: "Tenant",
        unitNumber: repair.unitNumber || null,
      };
    }

    // Collect handyman IDs to add to chat
    const handymanIds = [];

    // 1. Add assigned handyman if exists
    if (repair.assignedTo) {
      handymanIds.push(repair.assignedTo);
    }

    // 2. Find handymen with joinAllGroupChats enabled
    try {
      const allHandymen = await HandymanModel.getByOwner(landlordId);
      const handymenWithJoinAll = allHandymen.filter(
        (h) => h.joinAllGroupChats === true
      );
      handymenWithJoinAll.forEach((h) => {
        if (!handymanIds.includes(h.id)) {
          handymanIds.push(h.id);
        }
      });
    } catch (error) {
      console.warn("Could not fetch handymen for group chat:", error);
    }

    // Only create chat if we have at least one handyman
    if (handymanIds.length > 0) {
      await chatModel.createOrGetMaintenanceGroupChat({
        tenantId: repair.tenantId,
        landlordId: landlordId,
        handymanIds: handymanIds,
        ticketId: repair.id,
        ticketTitle: repair.title,
        unitNumber: tenantInfo.unitNumber,
        tenantName: tenantInfo.name,
      });
    }
  } catch (error) {
    // Don't fail the repair creation/update if chat creation fails
    console.error("Error ensuring maintenance group chat:", error);
  }
}

const repairModel = {
  async create(repairData) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const repair = {
      ...repairData,
      ownerId: user.uid,
      status: repairData.status || "open",
      actualCost: repairData.actualCost || 0,
      photos: repairData.photos || [],
      documents: repairData.documents || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), repair);
    const createdRepair = { id: docRef.id, ...repair };

    // Create maintenance group chat if needed
    await ensureMaintenanceGroupChat(createdRepair, user.uid);

    return {
      success: true,
      repair: createdRepair,
      message: "Repair ticket created successfully",
    };
  },

  async getByOwner(ownerId = null) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const userId = ownerId || user.uid;
    const q = query(
      collection(db, COLLECTION_NAME),
      where("ownerId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => {
      const data = d.data();

      // Helper to safely convert timestamps
      const safeToDate = (field) => {
        if (!field) return null;
        if (typeof field.toDate === "function") return field.toDate();
        if (field instanceof Date) return field;
        if (typeof field === "string") return new Date(field);
        if (typeof field === "number") return new Date(field);
        return null;
      };

      return {
        id: d.id,
        ...data,
        createdAt: safeToDate(data.createdAt) || new Date(),
        updatedAt: safeToDate(data.updatedAt) || new Date(),
        scheduledDate: safeToDate(data.scheduledDate),
        completedDate: safeToDate(data.completedDate),
      };
    });
  },

  async getById(repairId) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = doc(db, COLLECTION_NAME, repairId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Repair not found");
    const data = snap.data();

    // Helper to safely convert timestamps
    const safeToDate = (field) => {
      if (!field) return null;
      if (typeof field.toDate === "function") return field.toDate();
      if (field instanceof Date) return field;
      if (typeof field === "string") return new Date(field);
      if (typeof field === "number") return new Date(field);
      return null;
    };

    return {
      id: snap.id,
      ...data,
      category: normalizeRepairCategory(data.category),
      createdAt: safeToDate(data.createdAt) || new Date(),
      updatedAt: safeToDate(data.updatedAt) || new Date(),
      scheduledDate: safeToDate(data.scheduledDate),
      completedDate: safeToDate(data.completedDate),
    };
  },

  async update(repairId, updates) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    // Check if assignedTo is being updated - if so, call backend API to trigger email notification
    const isAssigningHandyman = updates.assignedTo !== undefined;
    const previousRepair = isAssigningHandyman
      ? await this.getById(repairId)
      : null;
    const isNewAssignment =
      isAssigningHandyman && previousRepair?.assignedTo !== updates.assignedTo;

    // If assigning or unassigning a handyman, call backend API first to trigger email notification
    if (isNewAssignment) {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        if (API_URL) {
          const token = await user.getIdToken();
          const endpoint = `${API_URL}/repairs/${repairId}`;

          // Build request body - only include assignedTo if it's a non-empty string
          const requestBody = {};
          if (
            updates.assignedTo &&
            typeof updates.assignedTo === "string" &&
            updates.assignedTo.trim() !== ""
          ) {
            requestBody.assignedTo = updates.assignedTo;
          } else if (
            updates.assignedTo === null ||
            updates.assignedTo === undefined ||
            updates.assignedTo === ""
          ) {
            // For unassignment, send empty string instead of null (backend expects string)
            requestBody.assignedTo = "";
          }

          console.log(
            "[RepairModel] Calling backend API:",
            endpoint,
            requestBody
          );

          const response = await fetch(endpoint, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(
              "[RepairModel] Backend API update failed:",
              response.status,
              errorText
            );
            console.warn(
              "[RepairModel] Continuing with Firestore update despite API failure"
            );
          } else {
            const result = await response.json();
            console.log("[RepairModel] Backend API update successful:", result);
          }
        } else {
          console.warn(
            "[RepairModel] REACT_APP_API_URL not configured, skipping backend API call"
          );
        }
      } catch (error) {
        // Don't fail the update if backend API call fails - still update Firestore
        console.error(
          "[RepairModel] Error calling backend API for handyman assignment:",
          error
        );
      }
    }

    // Update Firestore
    const ref = doc(db, COLLECTION_NAME, repairId);
    await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });

    // Get updated repair data for group chat creation
    const updatedRepair = await this.getById(repairId);
    const repairWithUpdates = { ...updatedRepair, ...updates };

    // Update maintenance group chat if needed (handyman assigned or joinAllGroupChats changed)
    await ensureMaintenanceGroupChat(repairWithUpdates, user.uid);

    return {
      success: true,
      repair: { id: repairId, ...updates, updatedAt: new Date() },
      message: "Repair updated successfully",
    };
  },

  async updateStatus(repairId, status, additionalUpdates = {}) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = doc(db, COLLECTION_NAME, repairId);
    const updates = {
      status,
      ...additionalUpdates,
      updatedAt: serverTimestamp(),
    };
    if (status === "completed") updates.completedDate = serverTimestamp();
    await updateDoc(ref, updates);
    return {
      success: true,
      repair: {
        id: repairId,
        status,
        ...additionalUpdates,
        updatedAt: new Date(),
      },
      message: "Repair status updated successfully",
    };
  },

  async assignHandyman(repairId, handymanId) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    // Call backend API to trigger email notification when assigning handyman
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      if (API_URL) {
        const token = await user.getIdToken();
        const endpoint = `${API_URL}/repairs/${repairId}`;

        console.log(
          "[RepairModel] Calling backend API to assign handyman:",
          endpoint,
          { assignedTo: handymanId }
        );

        const response = await fetch(endpoint, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            assignedTo: handymanId || null,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "[RepairModel] Backend API update failed:",
            response.status,
            errorText
          );
          console.warn(
            "[RepairModel] Continuing with Firestore update despite API failure"
          );
        } else {
          const result = await response.json();
          console.log("[RepairModel] Backend API update successful:", result);
        }
      } else {
        console.warn(
          "[RepairModel] REACT_APP_API_URL not configured, skipping backend API call"
        );
      }
    } catch (error) {
      // Don't fail the update if backend API call fails - still update Firestore
      console.error(
        "[RepairModel] Error calling backend API for handyman assignment:",
        error
      );
    }

    // Update Firestore
    const ref = doc(db, COLLECTION_NAME, repairId);
    await updateDoc(ref, {
      assignedTo: handymanId,
      status: "assigned",
      updatedAt: serverTimestamp(),
    });

    // Get updated repair data for group chat creation
    const updatedRepair = await this.getById(repairId);

    // Update maintenance group chat
    await ensureMaintenanceGroupChat(updatedRepair, user.uid);

    return {
      success: true,
      repair: {
        id: repairId,
        assignedTo: handymanId,
        status: "assigned",
        updatedAt: new Date(),
      },
      message: "Handyman assigned successfully",
    };
  },

  async unassignHandyman(repairId) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    // Call backend API to notify about unassignment
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      if (API_URL) {
        const token = await user.getIdToken();
        const endpoint = `${API_URL}/repairs/${repairId}`;

        console.log(
          "[RepairModel] Calling backend API to unassign handyman:",
          endpoint
        );

        // Send empty string instead of null (backend expects string type)
        const response = await fetch(endpoint, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            assignedTo: "",
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "[RepairModel] Backend API update failed:",
            response.status,
            errorText
          );
          console.warn(
            "[RepairModel] Continuing with Firestore update despite API failure"
          );
        } else {
          const result = await response.json();
          console.log("[RepairModel] Backend API update successful:", result);
        }
      } else {
        console.warn(
          "[RepairModel] REACT_APP_API_URL not configured, skipping backend API call"
        );
      }
    } catch (error) {
      // Don't fail the update if backend API call fails - still update Firestore
      console.error(
        "[RepairModel] Error calling backend API for handyman unassignment:",
        error
      );
    }

    // Update Firestore
    const ref = doc(db, COLLECTION_NAME, repairId);
    await updateDoc(ref, { assignedTo: null, updatedAt: serverTimestamp() });

    return { success: true, message: "Handyman unassigned successfully" };
  },

  async delete(repairId) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = doc(db, COLLECTION_NAME, repairId);
    await updateDoc(ref, { status: "cancelled", updatedAt: serverTimestamp() });
    return { success: true, message: "Repair cancelled successfully" };
  },
};

export default repairModel;
